import { type FC, type FormEvent, useState } from 'react';
import { useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import type { SingleValue } from 'react-select';
import ApiErrorBanner from '@/components/ApiErrorBanner';
import { toNumericId } from '@/helpers';
import type { Animal, AnimalSex } from '@/model/Animal.model';
import type { StudyApiId } from '@/model/Study.model';
import { getSexOptions } from '@/model/Animal.utils';
import { featuresSelector } from '@/support/Selectors';
import Select, { type SelectOption } from '@/components/UI/Select/Select';
import { useApiHook } from '@/support/Hooks/api/useApiHook';

const unknownSexSelector = createSelector([featuresSelector], (features) => ({
  unknownSexEnabled: features?.unknown_sex === true,
}));

interface UpdateSexProps {
  subjects: Array<Animal>;
  studyApiId: StudyApiId;
  handleCallback: (message: string) => void;
  closeModal: () => void;
}

const UpdateSex: FC<UpdateSexProps> = ({ subjects, studyApiId, handleCallback, closeModal }) => {
  const [sex, setSex] = useState<AnimalSex>('m');
  const [validationError, setValidationError] = useState<string | null>(null);

  const { unknownSexEnabled } = useSelector(unknownSexSelector);
  const sexOptions = getSexOptions(unknownSexEnabled);

  const {
    invoke: updateSex,
    loading: isLoading,
    response: updateResponse,
  } = useApiHook({
    endpoint: 'PATCH /api/v1/studies/{studyApiId}/animals/sex',
    invokeOnInit: false,
    options: { onError: { throw: false, capture: true, toast: false, slug: 'animal-sex-update' } },
  });

  const what = subjects.length > 1 ? 'animals' : 'animal';
  const whatDemonstrative = what === 'animals' ? 'these' : 'this';

  const submitForm = async (e: FormEvent) => {
    e.preventDefault();
    if (!unknownSexEnabled && sex === 'u') {
      setValidationError('Unknown sex is not available');
      return;
    }
    const animalIds = subjects.map((s) => toNumericId(s.id));
    const result = await updateSex({ path: { studyApiId }, body: { animal_ids: animalIds, sex } });
    if (result.type === 'success') {
      handleCallback('Successfully updated sex');
    }
  };

  const defaultOption = sexOptions.find(({ value }) => value === sex);

  return (
    <form onSubmit={submitForm} className="ui__narrow-modal" data-testid="update-sex-form">
      <div className="mb3 tc">
        <h3 className="normal f4 pb2" data-testid="update-sex-heading">
          Update sex of {whatDemonstrative} {what}?
        </h3>
      </div>
      {updateResponse?.type === 'error' && <ApiErrorBanner error={updateResponse.error} />}
      {validationError && (
        <p className="red" data-testid="update-sex-error">
          {validationError}
        </p>
      )}
      <div className="mb3" data-testid="update-sex-select">
        <Select
          defaultValue={defaultOption}
          options={sexOptions}
          isMulti={false}
          onChange={(option: SingleValue<SelectOption<string>>) => {
            if (option) {
              setSex(option.value as AnimalSex);
            }
          }}
          name="sex"
        />
      </div>
      <div>
        <button type="submit" disabled={isLoading} className="button w-100" data-testid="update-sex-submit">
          Update sex
        </button>
        <button
          type="button"
          className="button plain db w-100 mt2"
          onClick={closeModal}
          data-testid="update-sex-cancel"
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

export default UpdateSex;
